import { AGNT_IR_MAILTO } from '@tenants/definitions/agnt-inc/constants';

/**
 * Link/content data for the AGNT, Inc. footer.
 *
 * Ported from the redesign mock (`exp-world-holdings-AGNT`, the `<footer>` block
 * shared across every page). Kept as typed English data (edited via PRs,
 * localised at render by GTranslate), separate from the markup in `index.tsx` —
 * matching the AGNT content-page convention. Internal AGNT routes use app paths;
 * everything else (company sites, legal, social) links out.
 */

export interface FooterLink {
  label: string;
  href: string;
}

export type SocialIcon = 'facebook' | 'x' | 'linkedin' | 'instagram';

export interface FooterSocial {
  label: string;
  href: string;
  icon: SocialIcon;
}

export interface FooterColumn {
  heading: string;
  links: FooterLink[];
}

export interface FooterContent {
  /** "AGNT Companies" brand strip across the top of the footer. */
  brands: FooterLink[];
  description: string;
  social: FooterSocial[];
  columns: FooterColumn[];
  legal: FooterLink[];
}

export const FOOTER_CONTENT: FooterContent = {
  brands: [
    { label: 'eXp Realty', href: 'https://exprealty.com' },
    { label: 'eXp Commercial', href: 'https://expcommercial.com' },
    { label: 'NextHome', href: 'https://www.nexthome.com/' },
    { label: 'SUCCESS', href: 'https://www.success.com' },
    { label: 'Frame', href: 'https://framevr.io' },
  ],
  description:
    'AGNT, Inc. (Nasdaq: AGNT) is a multi-model real estate platform, with brands including eXp Realty®, eXp Commercial, NextHome™, FrameVR.io, and SUCCESS Enterprises.',
  social: [
    { label: 'Facebook', href: 'https://www.facebook.com/expworldholdings', icon: 'facebook' },
    { label: 'X', href: 'https://x.com/agntinc', icon: 'x' },
    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/agntinc', icon: 'linkedin' },
    { label: 'Instagram', href: 'https://www.instagram.com/agnt.inc/', icon: 'instagram' },
  ],
  columns: [
    {
      heading: 'Company',
      links: [
        { label: 'Why AGNT', href: '/why-agnt' },
        { label: 'Who We Are', href: '/who-we-are' },
        { label: 'Contact', href: AGNT_IR_MAILTO },
      ],
    },
    {
      heading: 'Investors',
      links: [
        { label: 'Press Releases', href: '/press-releases' },
        { label: 'Events & Presentations', href: '/events-and-presentations' },
        { label: 'Stock Information', href: '/stock-info' },
        { label: 'Financials', href: '/financials' },
        { label: 'Governance', href: '/governance' },
        { label: 'AGNT Businesses', href: '/agnt-businesses' },
        { label: 'Resources', href: '/resources' },
      ],
    },
    {
      heading: 'Get Started',
      links: [
        { label: 'Careers at AGNT', href: '/careers' },
        { label: 'Contact IR', href: AGNT_IR_MAILTO },
        { label: 'Newsroom', href: '/news' },
        { label: 'Investor Alerts', href: '#alerts' },
      ],
    },
  ],
  legal: [
    { label: 'Policies', href: '/policies-and-procedures' },
    { label: 'Privacy', href: '/privacy-policy' },
    { label: 'Terms', href: '/terms-of-service' },
    { label: 'Cookies', href: '/cookie-policy' },
    {
      label: 'Do Not Sell My Data',
      href: 'https://submit-irm.trustarc.com/services/validation/e6d7516d-df9c-4404-bd9f-21a2f0d2a6a4',
    },
  ],
};
